// @oasis-permission-inherits: /ai-edits/evals (evals:view)
import { Link, Navigate, createFileRoute } from "@tanstack/react-router";
import { CircleHelp } from "lucide-react";
import { Streamdown } from "streamdown";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { ComparisonOutputs } from "~/modules/agave-evals/comparison-outputs";
import { ComparisonPerformancePanel } from "~/modules/agave-evals/comparison-performance-panel";
import { AgaveEvalExternalButton, AgaveEvalSourceBadge } from "~/modules/agave-evals/components";
import { ExecutorComparisonOutputs } from "~/modules/agave-evals/executor-comparison-outputs";
import { formatAgaveEvalDate } from "~/modules/agave-evals/formatters";
import {
  harborComparisonRuns,
  isFrozenExecutorComparisonId,
  isMissingEval,
} from "~/modules/agave-evals/harbor-comparison";
import { isHarborPairwise } from "~/modules/agave-evals/harbor-pairwise";
import { HarborPairwisePanel } from "~/modules/agave-evals/harbor-pairwise-panel";
import {
  agaveEvalBackLabel,
  agaveEvalListKindSchema,
  type AgaveEvalListKind,
} from "~/modules/agave-evals/list-kinds";
import { getAgaveEvalComparisonQuery, getAgaveEvalRunQuery } from "~/modules/agave-evals/queries";
import type { AgaveEvalComparisonDetail } from "~/modules/agave-evals/schemas";

import "~/modules/agave-evals/optimization-reference.css";
import { usePageTitle } from "~/modules/page-title/page-title";

const searchSchema = z.object({
  kind: agaveEvalListKindSchema.optional(),
  legacy: z.boolean().optional().default(false),
});

export const Route = createFileRoute("/_dash/ai-edits/evals/comparisons/$comparisonId")({
  validateSearch: searchSchema,
  component: RouteComponent,
});

type JsonRecord = Record<string, unknown>;
type DeltaIntent = "score" | "usage";

function RouteComponent() {
  const { legacy } = Route.useSearch();
  const { comparisonId } = Route.useParams();
  usePageTitle(`Comparison ${comparisonId}`);
  return legacy ? <LegacyComparison /> : <OptimizationComparisonRedirect />;
}

function OptimizationComparisonRedirect() {
  const { comparisonId } = Route.useParams();
  const knownRuns = harborComparisonRuns(comparisonId);
  const { data, isLoading, error } = getAgaveEvalComparisonQuery.useQuery(
    { pathParams: { comparisonId } },
    { enabled: !knownRuns, retry: false }
  );
  const runs = knownRuns ?? data;
  if (runs) {
    return (
      <Navigate
        to="/ai-edits/evals"
        replace
        search={{
          legacy: false,
          optimizationView: "compare",
          baselineRunId: runs.baselineRunId,
          candidateRunId: runs.candidateRunId,
          comparisonId,
        }}
      />
    );
  }
  if (isLoading) {
    return <ComparisonSkeleton />;
  }
  return (
    <div className="space-y-4">
      <Link to="/ai-edits/evals" search={{ optimizationView: "compare" }}>
        Agave Optimization · Compare
      </Link>
      <p className="text-destructive">
        Could not resolve comparison: {error?.message ?? "Comparison not found"}
      </p>
    </div>
  );
}

function LegacyComparison() {
  const { comparisonId } = Route.useParams();
  const { kind, legacy } = Route.useSearch();
  const frozenExecutor = isFrozenExecutorComparisonId(comparisonId);
  const liveRuns = harborComparisonRuns(comparisonId);
  const {
    data: comparison,
    isLoading,
    error,
  } = getAgaveEvalComparisonQuery.useQuery(
    { pathParams: { comparisonId } },
    { retry: false, refetchInterval: false, refetchOnWindowFocus: false, refetchOnReconnect: false }
  );

  if (isLoading) {
    return <ComparisonSkeleton />;
  }

  if (!comparison && liveRuns && isMissingEval(error)) {
    return (
      <div className="agave-reference space-y-6">
        <ComparisonToolbar kind={kind} legacy={legacy} />
        <h1 className="text-xl font-bold">Main and PR comparison</h1>
        <p className="text-sm text-muted-foreground">
          Pairwise results are not available. Use Refresh to load new results. Check the workflow
          for progress or failures.
        </p>
        <AgaveEvalExternalButton href={liveRuns.workflowUrl} label="Workflow" />
        <ComparisonPerformancePanel {...liveRuns} prComparison />
        {frozenExecutor ? (
          <FrozenComparisonOutputs {...liveRuns} />
        ) : (
          <ComparisonOutputs {...liveRuns} live prComparison />
        )}
      </div>
    );
  }

  if (error || !comparison) {
    return (
      <div className="agave-reference space-y-5">
        <ComparisonToolbar kind={kind} legacy={legacy} />
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          <p className="font-medium">Failed to load eval comparison</p>
          {error && <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>}
        </div>
      </div>
    );
  }

  return (
    <div className="agave-reference space-y-6">
      <ComparisonToolbar
        kind={kind ?? (comparison.evalSuite === "chat" ? "harbor-chat" : "creation")}
        legacy={legacy}
      />
      <ComparisonHeader comparison={comparison} />
      <ComparisonPerformancePanel
        baselineRunId={comparison.baselineRunId}
        candidateRunId={comparison.candidateRunId}
        prComparison={Boolean(liveRuns)}
      />
      <SummaryPanel comparison={comparison} />
      {isHarborPairwise(comparison) ? (
        <HarborPairwisePanel comparison={comparison} />
      ) : (
        <OverallPanel comparison={comparison} />
      )}
      {frozenExecutor ? (
        <FrozenComparisonOutputs
          baselineRunId={comparison.baselineRunId}
          candidateRunId={comparison.candidateRunId}
        />
      ) : (
        <ComparisonOutputs
          key={comparison.comparisonId}
          baselineRunId={comparison.baselineRunId}
          candidateRunId={comparison.candidateRunId}
          prComparison={Boolean(liveRuns)}
        />
      )}
      <MetricDeltasPanel comparison={comparison} />
      <TokenUsagePanel comparison={comparison} />
      {comparison.evalSuite !== "chat" && <CaseChangesPanel comparison={comparison} />}
    </div>
  );
}

function FrozenComparisonOutputs({
  baselineRunId,
  candidateRunId,
}: {
  baselineRunId: string;
  candidateRunId: string;
}) {
  const options = {
    retry: false,
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  } as const;
  const baseline = getAgaveEvalRunQuery.useQuery({ pathParams: { runId: baselineRunId } }, options);
  const candidate = getAgaveEvalRunQuery.useQuery(
    { pathParams: { runId: candidateRunId } },
    options
  );

  if (baseline.error || candidate.error) {
    const error = baseline.error ?? candidate.error;
    return (
      <p className="ar-warning" role={isMissingEval(error) ? "status" : "alert"}>
        {isMissingEval(error)
          ? "Waiting for both frozen executor runs to be published."
          : `Could not load frozen executor runs: ${error?.message}`}
      </p>
    );
  }
  if (!baseline.data || !candidate.data) {
    return <p role="status">Loading frozen executor runs…</p>;
  }
  return <ExecutorComparisonOutputs runA={baseline.data} runB={candidate.data} />;
}

function ComparisonToolbar({ kind, legacy }: { kind?: AgaveEvalListKind; legacy: boolean }) {
  const sourceKind = kind ?? "creation";
  return (
    <div className="flex items-center justify-between gap-3">
      <Link
        to="/ai-edits/evals"
        search={{
          kind: sourceKind,
          legacy,
          optimizationView: sourceKind === "harbor-chat" ? "runs" : undefined,
        }}
        className="text-sm text-muted-foreground hover:underline"
      >
        {"<"} {agaveEvalBackLabel(sourceKind)}
      </Link>
      <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
        Refresh
      </Button>
    </div>
  );
}

function ComparisonHeader({ comparison }: { comparison: AgaveEvalComparisonDetail }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="break-all font-mono text-xl font-bold">{comparison.comparisonId}</h1>
          <Badge variant="outline">comparison</Badge>
          <AgaveEvalSourceBadge source={comparison.runSource} />
          {comparison.prNumber && <Badge variant="outline">PR #{comparison.prNumber}</Badge>}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span>{formatAgaveEvalDate(comparison.createdAt)}</span>
          <span className="font-mono">{shortSha(comparison.baselineGitSha)}</span>
          <span>{"->"}</span>
          <span className="font-mono">{shortSha(comparison.candidateGitSha)}</span>
          {(comparison.baseRef || comparison.headRef) && (
            <span>
              {comparison.baseRef ?? "baseline"} {"->"} {comparison.headRef ?? "candidate"}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <AgaveEvalExternalButton href={comparison.artifactUrl} label="Artifact" />
        <AgaveEvalExternalButton href={comparison.workflowUrl} label="Workflow" />
      </div>
    </div>
  );
}

function SummaryPanel({ comparison }: { comparison: AgaveEvalComparisonDetail }) {
  if (!comparison.summary && !comparison.summaryError) {
    return null;
  }

  return (
    <section className="rounded-md border p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">Summary</h2>
        {comparison.summaryModel && (
          <div className="font-mono text-xs text-muted-foreground">{comparison.summaryModel}</div>
        )}
      </div>
      {comparison.summary ? (
        <Streamdown className="prose prose-sm mt-3 max-w-none break-words text-sm leading-relaxed text-foreground [&_p:last-child]:mb-0">
          {comparison.summary}
        </Streamdown>
      ) : (
        <div className="mt-3 text-sm text-muted-foreground">
          LLM summary unavailable: {comparison.summaryError}
        </div>
      )}
    </section>
  );
}

function OverallPanel({ comparison }: { comparison: AgaveEvalComparisonDetail }) {
  const tracks = ["all", "regression", "capability"].map((track) => ({
    track,
    row: getRecord(comparison.overall, track),
  }));

  return (
    <section className="rounded-md border p-4">
      <h2 className="text-base font-semibold">Overall Scores</h2>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        {tracks.map(({ track, row }) => (
          <div key={track} className="rounded-md border bg-muted/15 p-3">
            <div className="text-xs font-medium uppercase text-muted-foreground">
              {formatLabel(track)}
            </div>
            <div className="mt-2 flex flex-wrap items-baseline gap-2">
              <span className="text-lg font-semibold">
                {formatPercent(getNumber(row, "candidate_score", "candidateScore"))}
              </span>
              <DeltaValue
                value={getNumber(row, "delta")}
                formatter={formatPointDelta}
                intent="score"
              />
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              Baseline {formatPercent(getNumber(row, "baseline_score", "baselineScore"))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TokenUsagePanel({ comparison }: { comparison: AgaveEvalComparisonDetail }) {
  const rows = comparison.usage;
  const llmCreditsRow = getMetricRow(comparison.metrics, "credits_llm");
  if (rows.length === 0 && !llmCreditsRow) {
    return null;
  }

  const summaryRows = [
    { label: "Total tokens", row: getUsageRow(rows, "total_tokens") },
    { label: "Planner input", row: getUsageRow(rows, "planner_input_tokens") },
    { label: "Planner output", row: getUsageRow(rows, "planner_output_tokens") },
  ];

  return (
    <section className="rounded-md border p-4">
      <div className="flex items-center gap-1.5">
        <h2 className="text-base font-semibold">Token Usage</h2>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="How token usage and LLM credits are calculated"
              className="text-muted-foreground hover:text-foreground"
            >
              <CircleHelp className="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            Token usage and LLM credits include only matched trial pairs where both baseline and
            candidate completed successfully. Pairs where only one side succeeded are excluded.
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {summaryRows.map(({ label, row }) => (
          <TokenUsageSummary key={label} label={label} row={row} />
        ))}
        {llmCreditsRow && <LlmCreditsSummary row={llmCreditsRow} />}
      </div>
      {rows.length > 0 && (
        <Collapsible className="mt-3">
          <CollapsibleTrigger className="text-sm text-muted-foreground hover:text-foreground">
            View detailed token usage
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-3 overflow-hidden rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usage</TableHead>
                  <TableHead className="text-right">Baseline</TableHead>
                  <TableHead className="text-right">Candidate</TableHead>
                  <TableHead className="text-right">Delta</TableHead>
                  <TableHead className="text-right">Delta %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={`${getString(row, "usage_id", "usageId") ?? "usage"}-${index}`}>
                    <TableCell className="whitespace-normal font-medium">
                      {usageLabel(getString(row, "usage_id", "usageId") ?? "usage")}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {formatInt(getNumber(row, "baseline_total", "baselineTotal"))}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {formatInt(getNumber(row, "candidate_total", "candidateTotal"))}
                    </TableCell>
                    <TableCell className="text-right">
                      <DeltaValue
                        value={getNumber(row, "delta")}
                        formatter={formatSignedInt}
                        intent="usage"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <DeltaValue
                        value={getNumber(row, "delta_percent", "deltaPercent")}
                        formatter={formatPercentDelta}
                        intent="usage"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CollapsibleContent>
        </Collapsible>
      )}
    </section>
  );
}

function TokenUsageSummary({ label, row }: { label: string; row: JsonRecord | null }) {
  const baseline = getNumber(row, "baseline_total", "baselineTotal");
  const candidate = getNumber(row, "candidate_total", "candidateTotal");
  const delta = getNumber(row, "delta");

  return (
    <div className="rounded-md border bg-muted/15 p-3">
      <div className="text-xs font-medium uppercase text-muted-foreground">{label}</div>
      <div className="mt-2 flex flex-wrap items-baseline gap-2">
        <span className="text-lg font-semibold">{formatInt(candidate)}</span>
        <DeltaValue value={delta} formatter={formatSignedInt} intent="usage" />
      </div>
      <div className="mt-1 text-xs text-muted-foreground">Baseline {formatInt(baseline)}</div>
    </div>
  );
}

function LlmCreditsSummary({ row }: { row: JsonRecord }) {
  const baseline = getMetricTotal(row, "baseline");
  const candidate = getMetricTotal(row, "candidate");
  const delta = baseline != null && candidate != null ? candidate - baseline : null;

  return (
    <div className="rounded-md border bg-muted/15 p-3">
      <div className="text-xs font-medium uppercase text-muted-foreground">LLM credits</div>
      <div className="mt-2 flex flex-wrap items-baseline gap-2">
        <span className="text-lg font-semibold">{formatCredits(candidate)}</span>
        <DeltaValue value={delta} formatter={formatCreditsDelta} intent="usage" />
      </div>
      <div className="mt-1 text-xs text-muted-foreground">Baseline {formatCredits(baseline)}</div>
    </div>
  );
}

function MetricDeltasPanel({ comparison }: { comparison: AgaveEvalComparisonDetail }) {
  const rows = comparison.metrics.filter((row) => {
    const id = getString(row, "metric_id", "metricId");
    return (
      id !== "credits_llm" &&
      !(
        comparison.pairwise &&
        isHarborPairwise(comparison) &&
        (id === "pairwise_candidate_preference" || id === "binary_instruction_adherence")
      )
    );
  });
  if (rows.length === 0) {
    return null;
  }
  const regressionRows = rows.filter((row) => getMetricKind(row) === "regression");
  const capabilityRows = rows.filter((row) => getMetricKind(row) === "capability");
  const otherRows = rows.filter((row) => {
    const metricKind = getMetricKind(row);
    return metricKind !== "regression" && metricKind !== "capability";
  });

  return (
    <section className="rounded-md border p-4">
      <h2 className="text-base font-semibold">Metric Deltas</h2>
      <div className="mt-3 grid gap-4 lg:grid-cols-2">
        <MetricDeltaColumn title="Regression" rows={regressionRows} />
        <MetricDeltaColumn title="Capability" rows={capabilityRows} />
        {otherRows.length > 0 && (
          <MetricDeltaColumn title="Other" rows={otherRows} className="lg:col-span-2" />
        )}
      </div>
    </section>
  );
}

function MetricDeltaColumn({
  title,
  rows,
  className,
}: {
  title: string;
  rows: JsonRecord[];
  className?: string;
}) {
  const changedCount = rows.filter((row) => {
    const delta = getMetricDelta(row).value;
    return delta != null && delta !== 0;
  }).length;

  return (
    <div className={cn("overflow-hidden rounded-md border", className)}>
      <div className="border-b bg-muted/30 px-3 py-2">
        <div className="text-sm font-semibold">{title}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          {changedCount} changed · {rows.length} total
        </div>
      </div>
      {rows.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Metric</TableHead>
              <TableHead className="text-right">Baseline</TableHead>
              <TableHead className="text-right">Candidate</TableHead>
              <TableHead className="text-right">Delta</TableHead>
              <TableHead className="text-right">Attempts</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, index) => {
              const metricId = getString(row, "metric_id", "metricId") ?? "metric";
              const delta = getMetricDelta(row);
              return (
                <TableRow key={`${title}-${metricId}-${index}`}>
                  <TableCell className="whitespace-normal font-medium">
                    {formatLabel(metricId)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatMetricScore(row, "baseline")}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatMetricScore(row, "candidate")}
                  </TableCell>
                  <TableCell className="text-right">
                    <DeltaValue value={delta.value} formatter={delta.formatter} intent="score" />
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatAttempts(row)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <div className="p-3 text-sm text-muted-foreground">No metrics.</div>
      )}
    </div>
  );
}

function CaseChangesPanel({ comparison }: { comparison: AgaveEvalComparisonDetail }) {
  const rows = comparison.cases.filter((row) => {
    const status = getString(row, "status");
    return status && status !== "unchanged";
  });
  if (rows.length === 0) {
    return null;
  }

  return (
    <section className="rounded-md border p-4">
      <h2 className="text-base font-semibold">Case Changes</h2>
      <div className="mt-3 overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Case</TableHead>
              <TableHead>Suite</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Trials</TableHead>
              <TableHead className="text-right">Pipeline failures</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, index) => {
              const caseName = getString(row, "case_name", "caseName") ?? "case";
              const suite = getString(row, "suite") ?? "-";
              const status = getString(row, "status") ?? "-";
              return (
                <TableRow key={`${suite}-${caseName}-${status}-${index}`}>
                  <TableCell className="whitespace-normal font-medium">{caseName}</TableCell>
                  <TableCell>{suite}</TableCell>
                  <TableCell>
                    <Badge
                      variant={status === "regressed" ? "destructive" : "outline"}
                      className="text-xs"
                    >
                      {status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatCaseTrials(row)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatPipelineFailures(row)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

function DeltaValue({
  value,
  formatter,
  intent,
}: {
  value: number | null;
  formatter: (value: number) => string;
  intent: DeltaIntent;
}) {
  if (value == null) {
    return <span className="text-xs text-muted-foreground">n/a</span>;
  }
  const positiveClass = intent === "usage" ? "text-destructive" : "text-emerald-700";
  const negativeClass = intent === "usage" ? "text-emerald-700" : "text-destructive";
  return (
    <span
      className={cn(
        "font-mono text-xs font-semibold",
        value > 0 && positiveClass,
        value < 0 && negativeClass,
        value === 0 && "text-muted-foreground"
      )}
    >
      {formatter(value)}
    </span>
  );
}

function getMetricDelta(row: JsonRecord) {
  const normalized = getNumber(row, "normalized_score_delta", "normalizedScoreDelta");
  if (normalized != null) {
    return { value: normalized, formatter: formatPointDelta };
  }
  return {
    value: getNumber(row, "mean_delta", "meanDelta"),
    formatter: formatNumberDelta,
  };
}

function getMetricKind(row: JsonRecord) {
  return getString(row, "metric_kind", "metricKind");
}

function formatMetricScore(row: JsonRecord, side: "baseline" | "candidate") {
  const normalized = getNumber(row, `${side}_normalized_score`, `${side}NormalizedScore`);
  if (normalized != null) {
    return formatPercent(normalized);
  }
  const mean = getNumber(row, `${side}_mean`, `${side}Mean`);
  if (mean != null) {
    return mean.toFixed(2);
  }
  const passRate = getNumber(row, `${side}_pass_rate`, `${side}PassRate`);
  if (passRate != null) {
    return formatPercent(passRate);
  }
  return "n/a";
}

function formatAttempts(row: JsonRecord) {
  return `${formatInt(getNumber(row, "baseline_attempt_count", "baselineAttemptCount"))}->${formatInt(
    getNumber(row, "candidate_attempt_count", "candidateAttemptCount")
  )}`;
}

function formatCaseTrials(row: JsonRecord) {
  return `${formatInt(getNumber(row, "baseline_trial_count", "baselineTrialCount"))}->${formatInt(
    getNumber(row, "candidate_trial_count", "candidateTrialCount")
  )}`;
}

function formatPipelineFailures(row: JsonRecord) {
  return `${formatArrayCount(row, "baseline_pipeline_failures", "baselinePipelineFailures")}->${formatArrayCount(
    row,
    "candidate_pipeline_failures",
    "candidatePipelineFailures"
  )}`;
}

function formatArrayCount(row: JsonRecord, ...keys: string[]) {
  const value = getValue(row, ...keys);
  return Array.isArray(value) ? String(value.length) : "0";
}

function getUsageRow(rows: JsonRecord[], usageId: string) {
  return rows.find((row) => getString(row, "usage_id", "usageId") === usageId) ?? null;
}

function getMetricRow(rows: JsonRecord[], metricId: string) {
  return rows.find((row) => getString(row, "metric_id", "metricId") === metricId) ?? null;
}

function getMetricTotal(row: JsonRecord, side: "baseline" | "candidate") {
  const mean = getNumber(row, `${side}_mean`, `${side}Mean`);
  const attempts = getNumber(row, `${side}_attempt_count`, `${side}AttemptCount`);
  return mean != null && attempts != null ? mean * attempts : null;
}

function getRecord(record: JsonRecord, ...keys: string[]): JsonRecord | null {
  const value = getValue(record, ...keys);
  return value && typeof value === "object" && !Array.isArray(value) ? (value as JsonRecord) : null;
}

function getValue(record: JsonRecord | null, ...keys: string[]) {
  if (!record) {
    return null;
  }
  for (const key of keys) {
    if (key in record) {
      return record[key];
    }
  }
  return null;
}

function getString(record: JsonRecord, ...keys: string[]) {
  const value = getValue(record, ...keys);
  return typeof value === "string" && value ? value : null;
}

function getNumber(record: JsonRecord | null, ...keys: string[]) {
  const value = getValue(record, ...keys);
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function formatPercent(value: number | null) {
  if (value == null) {
    return "n/a";
  }
  return `${(value * 100).toFixed(1)}%`;
}

function formatPointDelta(value: number) {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${(value * 100).toFixed(1)} pp`;
}

function formatPercentDelta(value: number) {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${(value * 100).toFixed(1)}%`;
}

function formatNumberDelta(value: number) {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}`;
}

function formatSignedInt(value: number) {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toLocaleString()}`;
}

function formatCredits(value: number | null) {
  if (value == null) {
    return "n/a";
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function formatCreditsDelta(value: number) {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${formatCredits(value)}`;
}

function formatInt(value: number | null) {
  if (value == null) {
    return "n/a";
  }
  return value.toLocaleString();
}

function formatLabel(value: string) {
  return value
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function usageLabel(value: string) {
  const labels: Record<string, string> = {
    total_tokens: "Total tokens",
    planner_total_tokens: "Planner total",
    planner_input_tokens: "Planner input",
    planner_output_tokens: "Planner output",
    planner_cache_creation_input_tokens: "Planner cache write",
    planner_cache_read_input_tokens: "Planner cache read",
    planner_reasoning_output_tokens: "Planner reasoning",
    judge_total_tokens: "Judge total",
    judge_prompt_tokens: "Judge prompt",
    judge_candidate_tokens: "Judge candidate",
    judge_cached_tokens: "Judge cached",
    judge_thoughts_tokens: "Judge thoughts",
    judge_tool_use_prompt_tokens: "Judge tool prompt",
  };
  return labels[value] ?? formatLabel(value);
}

function shortSha(value: string) {
  return value.slice(0, 12);
}

function ComparisonSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-48 rounded-md" />
      <Skeleton className="h-28 w-full rounded-md" />
      <Skeleton className="h-48 w-full rounded-md" />
      <Skeleton className="h-72 w-full rounded-md" />
    </div>
  );
}
